import {
	Column,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
} from 'typeorm';
import { BaseEntity } from './base.entity';
import { MSfpSmartFilterGroupRbacRoleEntity } from './m-sfp-smart-filter-group-rbac-role.entity';
import { MSfpSmartFilterRbacRoleEntity } from './m-sfp-smart-filter-rbac-role.entity';
import { MoverGroupRbacRoleEntity } from './mover-group-rbac-role.entity';
import { MoverRbacRoleEntity } from './mover-rbac-role.entity';
import { PreferenceRbacRoleEntity } from './preference-rbac-role.entity';
import { RbacActionRoleEntity } from './rbac-action-role.entity';

@Index('_name', ['name'], { unique: true })
@Index('idx_rbac_role_parent_role', ['fkRbacRoleIdParent'], {})
@Index('idx_rbac_role_manager', ['fkRbacRoleIdManager'], {})
@Entity('_rbac_role')
export class RbacRoleEntity extends BaseEntity {
	@Column('varchar', { name: '_name', unique: true, length: 255 })
	public name!: string;

	/**
	 * Parent role for PERMISSION INHERITANCE (PD-15546). A role with a
	 * parent ("child role") holds every action its parent holds, plus its
	 * own `_rbac_action__role` rows (additive overrides), minus its
	 * `rbac_role_action_exclusion` rows (subtractive overrides). The merge
	 * happens at read time in every permission resolver, so a grant made
	 * to the parent later (UI or seeding migration) reaches the child with
	 * no copying.
	 *
	 * One level only: a parent cannot itself have a parent, and a role
	 * that is a parent cannot be given one — enforced in
	 * `RbacService.updateRole` / `createRole`.
	 *
	 * Until PD-15546 this column carried the softphone manager hierarchy;
	 * that moved to `fkRbacRoleIdManager` (see the migration
	 * `MoveRoleManagerHierarchyOffParentColumn`).
	 */
	@Column('int', {
		name: 'fk__rbac_role__id_parent',
		nullable: true,
		unsigned: true,
	})
	public fkRbacRoleIdParent!: number | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([
		{ name: 'fk__rbac_role__id_parent', referencedColumnName: 'id' },
	])
	public parentRole!: RbacRoleEntity | null;

	/**
	 * Manager role — the role that manages/oversees this role. One-hop
	 * traversal only for v1. Used by the "manager call view" feature so
	 * users in the manager role can see calls handled by users in this
	 * role (e.g. "sales manager" role manages the "sales rep" role).
	 * Has NO effect on permissions — that is `fkRbacRoleIdParent`.
	 */
	@Column('int', {
		name: 'fk__rbac_role__id_manager',
		nullable: true,
		unsigned: true,
	})
	public fkRbacRoleIdManager!: number | null;

	@ManyToOne(() => RbacRoleEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'RESTRICT',
		nullable: true,
	})
	@JoinColumn([
		{ name: 'fk__rbac_role__id_manager', referencedColumnName: 'id' },
	])
	public managerRole!: RbacRoleEntity | null;

	@OneToMany(
		() => MoverRbacRoleEntity,
		(moverRbacRole) => moverRbacRole.fkRbacRole,
		{},
	)
	public moverRbacRoles!: Array<MoverRbacRoleEntity>;

	@OneToMany(
		() => MoverGroupRbacRoleEntity,
		(moverGroupRbacRole) => moverGroupRbacRole.fkRbacRole,
		{},
	)
	public moverGroupRbacRoles!: Array<MoverGroupRbacRoleEntity>;

	@OneToMany(
		() => PreferenceRbacRoleEntity,
		(preferenceRbacRole) => preferenceRbacRole.fkRbacRole,
		{},
	)
	public preferenceRbacRoles!: Array<PreferenceRbacRoleEntity>;

	@OneToMany(
		() => RbacActionRoleEntity,
		(rbacActionRole) => rbacActionRole.fkRbacRole,
		{},
	)
	public rbacActionRoles!: Array<RbacActionRoleEntity>;

	@OneToMany(
		() => MSfpSmartFilterRbacRoleEntity,
		(mSfpSmartFilterRbacRole) => mSfpSmartFilterRbacRole.fkRbacRole,
		{},
	)
	public mSfpSmartFilterRbacRoles!: Array<MSfpSmartFilterRbacRoleEntity>;

	@OneToMany(
		() => MSfpSmartFilterGroupRbacRoleEntity,
		(mSfpSmartFilterGroupRbacRole) => mSfpSmartFilterGroupRbacRole.fkRbacRole,
		{},
	)
	public mSfpSmartFilterGroupRbacRoles!: Array<MSfpSmartFilterGroupRbacRoleEntity>;
}
